/**
 * Kitchen color configuration, per tenant.
 *
 * Maps a tenant id to its kitchen codes' specific RGBA color strings. This is
 * the single place to manage kitchen colors across all clients.
 *
 * To onboard a new client's kitchens:
 *   1. Add a tenant entry below, keyed by its tenant id (same id read from
 *      `localStorage.tenantId` / sent as the `Tenant` header). Casing doesn't
 *      matter — the lookup below normalizes both sides to lowercase.
 *   2. Under it, add '<KITCHEN_CODE>': 'rgba(r, g, b, alpha)' per kitchen.
 *   3. Pick colors visually distinct from each other within that tenant.
 *
 * A tenant with no entry here — or a kitchen code not listed under its
 * tenant — automatically receives the next available color from
 * VIBRANT_KITCHEN_PALETTE below (rotated in RenderCell.view.tsx), so every
 * tenant gets the same vibrant look by default; adding a tenant entry here
 * instead gives it stable, intentional per-kitchen colors rather than
 * whatever order its kitchens happen to render in.
 */
const MCCORMICK_KITCHEN_COLORS: Record<string, string> = {
  PKN01: 'rgba(255, 0, 0, 0.8)', // red
  PKN02: 'rgba(255, 255, 0, 0.8)', // yellow
  PKN03: 'rgba(0, 255, 255, 0.7)', // cyan
  PKN04: 'rgba(148, 0, 211, 0.8)', // purple
  PKN05: 'rgba(255, 182, 193, 0.9)', // pink
  PKN06: 'rgba(0, 255, 0, 0.7)', // bright green
  PKN07: 'rgba(255, 200, 0, 0.8)', // amber/gold
  PKN08: 'rgba(255, 0, 255, 0.8)', // magenta
  PKN12: 'rgba(0, 100, 0, 0.8)', // dark green
  // 'P' is a resource code that otherwise falls through to the fallback
  // palette (slot 0) in RenderCell.view.tsx. Pin it to neutral grey.
  P: 'rgba(90, 90, 90, 0.85)',
}

/** The canonical vibrant palette, in rotation order — reused as the generic
 *  fallback in RenderCell.view.tsx for any tenant/kitchen not pinned below,
 *  so every tenant looks consistent by default (excludes the neutral grey
 *  'P' catch-all, which isn't a kitchen hue). */
export const VIBRANT_KITCHEN_PALETTE: string[] = Object.entries(
  MCCORMICK_KITCHEN_COLORS
)
  .filter(([code]) => code !== 'P')
  .map(([, color]) => color)

const KITCHEN_COLORS_BY_TENANT: Record<string, Record<string, string>> = {
  mccormick: MCCORMICK_KITCHEN_COLORS,
  // 'tst' is a McCormick test/staging environment running the same kitchen
  // data under a different tenant id — same colors, not a separate client.
  tst: MCCORMICK_KITCHEN_COLORS,
}

// Normalize the config's own keys to lowercase too, so a tenant id typo'd
// with different casing above still resolves correctly.
const normalizeTenantKeys = (
  byTenant: Record<string, Record<string, string>>
): Record<string, Record<string, string>> =>
  Object.fromEntries(
    Object.entries(byTenant).map(([tenantId, colors]) => [
      tenantId.toLowerCase(),
      colors,
    ])
  )

// Tenant switching does a full page reload (see TenantList.page.tsx), so
// resolving this once at module load is safe — it's never stale.
const currentTenantId = (
  typeof window !== 'undefined' ? window.localStorage.getItem('tenantId') : null
)?.toLowerCase()

/** Pinned kitchen colors for the current tenant only. */
export const KITCHEN_COLORS: Record<string, string> =
  (currentTenantId &&
    normalizeTenantKeys(KITCHEN_COLORS_BY_TENANT)[currentTenantId]) ||
  {}
